import {
  Box,
  Flex,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import { useMemo, useState } from "react";
import { solAmountFormatter, usdAmountFormatter } from "../../helpers/formatter";
import { normalizeTokenImageUrl } from "../../helpers/tokenImage";
import { ThemeColor } from "../../theme/palette";
import { StakeTokenOption } from "./buildRestakeTokenOptions";

interface StakeTokenSelectModalProps {
  isOpen: boolean;
  onClose: () => void;
  options: StakeTokenOption[];
  selectedSymbol: string;
  onSelect: (symbol: string) => void;
  isLoading?: boolean;
}

export const StakeTokenSelectModal = ({
  isOpen,
  onClose,
  options,
  selectedSymbol,
  onSelect,
  isLoading = false,
}: StakeTokenSelectModalProps) => {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (opt) =>
        opt.symbol.toLowerCase().includes(q) ||
        opt.name.toLowerCase().includes(q) ||
        (q.length > 2 && opt.mint.toLowerCase().includes(q))
    );
  }, [options, query]);

  const handleSelect = (symbol: string) => {
    onSelect(symbol);
    setQuery("");
    onClose();
  };

  const handleClose = () => {
    setQuery("");
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.700" />
      <ModalContent
        bg="#131B26"
        border="1px solid"
        borderColor="whiteAlpha.200"
        borderRadius="2xl"
        w="100%"
        maxW="640px"
        mx={4}
        maxH="85vh"
      >
        <ModalBody p={4}>
          <InputGroup mb={3} size="md">
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="whiteAlpha.600" />
            </InputLeftElement>
            <Input
              placeholder="Search token"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              bg="#0c1117"
              border="1px solid"
              borderColor="whiteAlpha.200"
              color="white"
              _placeholder={{ color: "whiteAlpha.500" }}
              _focus={{ borderColor: ThemeColor.KAIROS, boxShadow: "none" }}
            />
          </InputGroup>

          <Box maxH="360px" overflowY="auto" mx={-1} px={1}>
            {isLoading && filtered.length === 0 ? (
              <Flex justify="center" py={10}>
                <Spinner color={ThemeColor.KAIROS} />
              </Flex>
            ) : filtered.length === 0 ? (
              <Text py={8} textAlign="center" color="whiteAlpha.600" fontSize="sm">
                No tokens found
              </Text>
            ) : (
              <VStack spacing={1} align="stretch">
                {filtered.map((opt) => {
                  const isSelected =
                    opt.symbol.toLowerCase() === selectedSymbol.toLowerCase();
                  return (
                    <Flex
                      key={opt.mint}
                      as="button"
                      type="button"
                      w="100%"
                      align="center"
                      justify="space-between"
                      gap={3}
                      px={3}
                      py={3}
                      borderRadius="xl"
                      bg={isSelected ? "rgba(95, 244, 244, 0.1)" : "transparent"}
                      border="1px solid"
                      borderColor={isSelected ? "rgba(95, 244, 244, 0.35)" : "transparent"}
                      transition="background 0.15s ease, border-color 0.15s ease"
                      _hover={{
                        bg: isSelected ? "rgba(95, 244, 244, 0.12)" : "whiteAlpha.100",
                      }}
                      onClick={() => handleSelect(opt.symbol)}
                    >
                      <Flex align="center" gap={3} minW={0}>
                        <Image
                          src={normalizeTokenImageUrl(opt.imageUri)}
                          boxSize="36px"
                          borderRadius="full"
                          flexShrink={0}
                          fallbackSrc="/unknown.svg"
                        />
                        <Box textAlign="left" minW={0}>
                          <Text fontWeight="semibold" color="white" fontSize="sm">
                            {opt.symbol}
                          </Text>
                          <Text
                            fontSize="xs"
                            color="whiteAlpha.600"
                            noOfLines={1}
                          >
                            {opt.name}
                          </Text>
                        </Box>
                      </Flex>
                      <Box textAlign="right" flexShrink={0}>
                        <Text fontSize="sm" fontWeight="medium" color="white">
                          {solAmountFormatter.format(opt.balance)}
                        </Text>
                        {opt.value != null && opt.value > 0 && (
                          <Text fontSize="xs" color="whiteAlpha.600">
                            ${usdAmountFormatter.format(opt.value)}
                          </Text>
                        )}
                      </Box>
                    </Flex>
                  );
                })}
              </VStack>
            )}
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
